import { installedItems } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";
import { camelCase, mapKeys, mapValues } from "lodash-es";

import { pagerdutyMcpTools } from "./mcp-tools.ts";
import { PagerdutyRegions } from "./regions.ts";
import type { PagerdutyAiIntegration } from "./types.ts";

// Measured on a US account: a new token worked on the MCP server at once.
const pagerdutyPropagationHint = propagationHint({
  resource: "PagerDuty",
  normalWait: "1 to 5 seconds",
  maxWait: "30 seconds",
  pollInterval: "5 seconds",
});

const prefixArgs = ["pagerduty-ai", "permission"];

const accessPrompt = `Request access to a PagerDuty account by calling with argv:

  [<account>, <scope>..., "--reason", <reason>]

where:

  - account: The PagerDuty account, as installed in P0
  - scope: One or more PagerDuty OAuth scopes (e.g. 'incidents.read',
    'services.read'). Add the scopes of the tools you intend to call and no
    others. Use the list tool to enumerate them. P0 adds 'users.read', which
    every tool of this server needs.
  - reason: The reason access is needed

Every tool is listed whether or not your access covers it; PagerDuty refuses a
call that your scopes do not cover.

Calls act as an app, not as a user. Actions and filters that need the current
user fail: for example browse_users with action 'get', and browse_incidents
with request_scope 'assigned' or 'teams'.
`;

const pagerdutyAiMcp = ({
  label,
  mcpHost,
}: {
  label: string;
  mcpHost: string;
}): McpServerSpec & { type: "external" } => ({
  type: "external",
  label: `PagerDuty (${label})`,
  credential: {
    type: "vault",
    // Each grant stages its own secret name; read that rather than rebuilding
    // the naming scheme here.
    path: "{{ request.generated.secretName }}",
  },
  tools: { type: "manual", tools: pagerdutyMcpTools },
  // The token's scopes decide what PagerDuty allows.
  authorization: "upstream",
  url: `https://${mcpHost}/mcp`,
  access: {
    prompt: accessPrompt,
    prefixArgs,
    propagationHint: pagerdutyPropagationHint,
  },
  list: {
    fields: {
      account: {
        description: "PagerDuty account",
        required: [],
      },
      policies: {
        description:
          "PagerDuty OAuth scope that a token can hold, listed for one account",
        required: ["account"],
      },
    },
    prefixArgs,
  },
});

const serverIdFor = (region: string) => camelCase(`pagerduty-${region}`);

/** One predefined MCP server per PagerDuty region, keyed e.g. `pagerdutyUs`.
 * An install adds the server for its account's region. */
export const pagerdutyAiMcpServers = mapKeys(
  mapValues(PagerdutyRegions, pagerdutyAiMcp),
  (_, region) => serverIdFor(region)
);

const hasInstalledRegion =
  (region: string) =>
  (installed: Record<string, NewConfig>): boolean =>
    Object.values(
      installedItems(
        "credential",
        (installed["pagerduty-ai"] ?? {}) as PagerdutyAiIntegration
      )
    ).some((item) => item?.region.type === region);

/** Whether each of {@link pagerdutyAiMcpServers} can be added: only when a
 * PagerDuty account in its region is installed. */
export const pagerdutyAiMcpServerAvailability = mapKeys(
  mapValues(PagerdutyRegions, (_, region) => hasInstalledRegion(region)),
  (_, region) => serverIdFor(region)
);
